<script setup lang="ts">
  import { computed } from 'vue';
  import { DocumentSendFormat, type DocumentSendListItem } from '@/entities/DocumentSend';
  import SettingsIcon from '@/shared/assets/icons/SettingsIcon.svg';
  import TableTrashIcon from '@/shared/assets/icons/TableTrashIcon.svg';
  import { HoverListTooltip, IconButton, Tooltip } from '@/shared/ui';

  const SP_DAY_FORMATTER = new Intl.DateTimeFormat('pt-BR', {
    timeZone: 'America/Sao_Paulo',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });

  const formatSendDate = (iso: string): string => {
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? iso : SP_DAY_FORMATTER.format(d);
  };

  interface FormatBadge {
    format: DocumentSendFormat;
    label: string;
  }

  const ELECTRONIC_BADGE: FormatBadge = {
    format: DocumentSendFormat.ELECTRONIC,
    label: 'Eletrônico',
  };
  const PHYSICAL_BADGE: FormatBadge = {
    format: DocumentSendFormat.PHYSICAL,
    label: 'Físico',
  };

  const props = defineProps<{
    item: DocumentSendListItem;
  }>();

  const emit = defineEmits<{
    (e: 'view-clicked', id: number): void;
    (e: 'delete-clicked', id: number): void;
  }>();

  const recipientsLabel = computed(() => `${props.item.recipients.length} destinatários`);

  const recipientsTitle = computed(() => `Destinatários (${props.item.recipients.length})`);

  // recipients are plain names and can repeat, so the index keeps the list keys unique.
  const recipientItems = computed(() =>
    props.item.recipients.map((name, index) => ({ id: index, text: name }))
  );

  const getFormatBadges = (format: DocumentSendFormat): FormatBadge[] => {
    switch (format) {
      case DocumentSendFormat.ELECTRONIC:
        return [ELECTRONIC_BADGE];
      case DocumentSendFormat.PHYSICAL:
        return [PHYSICAL_BADGE];
      case DocumentSendFormat.ELECTRONIC_PHYSICAL:
        return [ELECTRONIC_BADGE, PHYSICAL_BADGE];
      default:
        return [];
    }
  };

  const getFormatBadgeClass = (badge: FormatBadge) =>
    badge.format === DocumentSendFormat.ELECTRONIC
      ? 'bg-[var(--color-badge-purple-02)] text-[var(--color-badge-purple)]'
      : 'bg-[var(--color-badge-orange-30)] text-[var(--color-badge-orange)]';
</script>

<template>
  <tr
    :data-testid="`document-send-control-row-${item.id}`"
    class="table-row-hover transition-colors duration-300 ease-out"
  >
    <td class="pl-6 pr-4 py-4.5 text-sm text-gray-07 text-left">
      {{ item.title }}
    </td>
    <td class="pl-6 pr-4 py-4.5 text-sm text-gray-07 text-left whitespace-nowrap">
      {{ formatSendDate(item.sendDate) }}
    </td>
    <td class="pl-6 pr-4 py-4.5 text-sm text-gray-07 text-left">
      {{ item.sender.name }}
    </td>
    <td class="pl-6 pr-4 py-4.5 text-sm text-gray-07 text-left">
      <HoverListTooltip
        v-if="item.recipients.length > 1"
        :label="recipientsLabel"
        :title="recipientsTitle"
        :items="recipientItems"
        test-id="document-send-recipients-tooltip"
      />
      <span v-else>{{ item.recipients[0] ?? '-' }}</span>
    </td>
    <td class="pl-6 pr-4 py-4.5 text-sm text-gray-07 text-left">
      <div class="flex flex-wrap gap-2">
        <span
          v-for="badge in getFormatBadges(item.format)"
          :key="badge.format"
          :class="getFormatBadgeClass(badge)"
          class="inline-flex rounded-full px-3 py-1 text-xs font-semibold"
        >
          {{ badge.label }}
        </span>
      </div>
    </td>
    <td class="px-6 py-4.5 text-sm text-gray-xx-02 text-left">
      <div class="flex items-center gap-3 justify-end">
        <Tooltip
          content="Controle"
          placement="top"
          class="flex items-center"
        >
          <IconButton
            :src="SettingsIcon"
            alt="Controle"
            @click="emit('view-clicked', item.id)"
          />
        </Tooltip>
        <Tooltip
          content="Deletar"
          placement="top"
          class="flex items-center"
        >
          <IconButton
            :src="TableTrashIcon"
            alt="Deletar"
            :on-click="() => emit('delete-clicked', props.item.id)"
          />
        </Tooltip>
      </div>
    </td>
  </tr>
</template>
